import React, { useContext } from 'react';
import { NodeProps } from 'reactflow';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { ReactComponent as Braille } from '../../../../../images/icons/braille-solid.svg';
import { StyledLabel } from '../../../../../shared/form/FieldLabel';
import { Checkbox } from '../../../../../shared/form/inputs/Checkbox';
import { getNullSelectOption, Select } from '../../../../../shared/form/inputs/Select';
import { UnitFieldContext } from '../../units/UnitFieldContext';
import { useTree } from '../map-helpers/hooks/useTree';
import { getCapacityString, getFlowRateString, getPressureDropString } from '../map-helpers/mapHelpers';
import { ManifoldMapNodeData } from '../mapNode';
import { NukePopover } from '../NukePopover';
import { TreeNode, TreeNodeType } from '../treeNode';
import { MapFlowNode } from './MapFlowNode';

export const Manifold = ({
  data: {
    treeNodeId,
    manifoldIndex,
    flowRateInLitresPerMinute,
    pressureDropInKilopascals,
    pumpOnManifold,
    pumpToUfh,
    state,
    markedForDeletion,
  },
}: NodeProps<ManifoldMapNodeData>) => {
  const { tree, replaceNode } = useTree();
  const { unit } = assertNotNull(useContext(UnitFieldContext));

  const onChangeManifold = (newManifoldIndex: number | null) => {
    const nodeToReplace = tree[treeNodeId];
    if (nodeToReplace.nodeType === TreeNodeType.Manifold) {
      const newNode: TreeNode = {
        ...nodeToReplace,
        fieldData: {
          manifoldIndex: newManifoldIndex,
          pumpOnManifold,
        },
      };
      replaceNode(treeNodeId, newNode);
    }
  };

  const handleChangePumpOnManifold = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    onChangePumpOnManifold(Boolean(value));
  };

  const onChangePumpOnManifold = (newPumpOnManifoldValue: boolean) => {
    const nodeToReplace = tree[treeNodeId];
    if (nodeToReplace.nodeType === TreeNodeType.Manifold) {
      const newNode: TreeNode = {
        ...nodeToReplace,
        fieldData: {
          manifoldIndex,
          pumpOnManifold: newPumpOnManifoldValue,
        },
      };
      replaceNode(treeNodeId, newNode);
    }
  };

  const manifoldOptions = unit.manifolds
    .filter(manifold => !manifold.isRadiator)
    .map((manifold, index) => ({ value: index, text: manifold.name }));

  const flowRate = getFlowRateString(flowRateInLitresPerMinute, null);
  const pressureDrop = getPressureDropString(pressureDropInKilopascals);
  const capacity = pumpOnManifold ? getCapacityString(pumpToUfh) : '';

  const checkboxFieldName = `manifold-checkbox-${treeNodeId}`;

  return (
    <MapFlowNode
      icon={<ManifoldIcon />}
      stats={[flowRate, pressureDrop, capacity]}
      hasSource={false}
      hasTarget={true}
      title="Manifold"
      state={state}
      markedForDeletion={markedForDeletion}
      popoverButtons={<NukePopover nodeId={treeNodeId} />}
    >
      <Select
        name="manifold"
        value={manifoldIndex}
        options={[getNullSelectOption<number>(), ...manifoldOptions]}
        isSmall={true}
        onChange={onChangeManifold}
      />
      <StyledLabel htmlFor={checkboxFieldName}>PM1?</StyledLabel>
      <Checkbox name={checkboxFieldName} onChange={handleChangePumpOnManifold} checked={Boolean(pumpOnManifold)} />
    </MapFlowNode>
  );
};

const ManifoldIcon = styled(Braille)`
  height: 1em;
  width: 1em;
`;
